import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Building2, Home, Shield, DollarSign, TrendingUp, Users, Award,
  ArrowLeft, Mail, Phone, MapPin, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentPortalDashboard() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [agentId, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) { navigate('/login'); return; }
      const res = await axios.get(`${API}/api/portal/agent/${agentId}?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else if (err.response?.status === 404) toast.error('Agent not found');
      else toast.error('Failed to load agent data');
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">No data available for this agent.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const { agent, insurance_agent, mortgage_stats, insurance_stats, combined, mortgage_commissions, insurance_commissions } = data;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="agent-portal-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agents')} data-testid="back-to-agents">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center">
            <Users className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="agent-name">
              {agent.name}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              {agent.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{agent.email}</span>}
              {agent.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{agent.phone}</span>}
              {agent.address && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{agent.address}</span>}
            </div>
            <div className="flex gap-2 mt-1.5">
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <Home className="w-3 h-3 mr-1" /> Mortgage
              </Badge>
              {insurance_agent && (
                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                  <Shield className="w-3 h-3 mr-1" /> Insurance
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Year:</span>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="agent-year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Combined Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-white" /></div>
              <div>
                <p className="text-xs text-slate-300 uppercase tracking-wider">Total Commissions</p>
                <p className="text-xl font-bold text-white font-mono" data-testid="stat-total-comm">{formatCurrency(combined.total_commissions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Award className="w-5 h-5 text-white" /></div>
              <div>
                <p className="text-xs text-emerald-100 uppercase tracking-wider">Agent Earnings</p>
                <p className="text-xl font-bold text-white font-mono" data-testid="stat-agent-earn">{formatCurrency(combined.agent_earnings)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
              <div>
                <p className="text-xs text-blue-100 uppercase tracking-wider">Brokerage Earnings</p>
                <p className="text-xl font-bold text-white font-mono" data-testid="stat-brokerage-earn">{formatCurrency(combined.brokerage_earnings)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Home className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Mortgage Deals</p>
                <p className="text-xl font-bold text-slate-900 font-mono" data-testid="stat-mortgage-count">{mortgage_stats.transaction_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Insurance Deals</p>
                <p className="text-xl font-bold text-slate-900 font-mono" data-testid="stat-insurance-count">{insurance_stats.transaction_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Division Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Home className="w-4 h-4 text-blue-600" /> Mortgage Division</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xs text-slate-500">Gross Commission</p><p className="text-lg font-bold font-mono">{formatCurrency(mortgage_stats.total_commissions)}</p></div>
              <div><p className="text-xs text-slate-500">Agent Earnings</p><p className="text-lg font-bold text-emerald-600 font-mono">{formatCurrency(mortgage_stats.agent_earnings)}</p></div>
              <div><p className="text-xs text-slate-500">Brokerage</p><p className="text-lg font-bold text-blue-600 font-mono">{formatCurrency(mortgage_stats.brokerage_earnings)}</p></div>
            </div>
            {mortgage_stats.total_volume > 0 && (
              <p className="text-xs text-slate-400 text-center mt-3">Total Mortgage Volume: {formatCurrency(mortgage_stats.total_volume)}</p>
            )}
          </CardContent>
        </Card>
        <Card className={!insurance_agent ? 'opacity-50' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-600" /> Insurance Division
              {!insurance_agent && <Badge variant="outline" className="text-xs ml-2">No Insurance License</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xs text-slate-500">Gross Commission</p><p className="text-lg font-bold font-mono">{formatCurrency(insurance_stats.total_commissions)}</p></div>
              <div><p className="text-xs text-slate-500">Agent Earnings</p><p className="text-lg font-bold text-emerald-600 font-mono">{formatCurrency(insurance_stats.agent_earnings)}</p></div>
              <div><p className="text-xs text-slate-500">Brokerage</p><p className="text-lg font-bold text-blue-600 font-mono">{formatCurrency(insurance_stats.brokerage_earnings)}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Details */}
      <Tabs defaultValue="mortgage" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mortgage" data-testid="tab-mortgage-comm">Mortgage ({mortgage_commissions.length})</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance-comm">Insurance ({insurance_commissions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="mortgage">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Mortgage Commissions — {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {mortgage_commissions.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No mortgage commissions for {selectedYear}</p>
              ) : (
                <div className="overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Lender</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Mortgage</TableHead>
                        <TableHead className="text-right">Gross Comm.</TableHead>
                        <TableHead className="text-right">Agent Split</TableHead>
                        <TableHead className="text-right">Agent Comm.</TableHead>
                        <TableHead className="text-right">Brokerage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mortgage_commissions.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">{c.statement_date}</TableCell>
                          <TableCell className="text-sm font-medium">{c.lender}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{c.description}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{formatCurrency(c.mortgage_loan)}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{formatCurrency(c.gross_commission)}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{c.agent_split_percentage}%</TableCell>
                          <TableCell className="text-right text-sm font-mono text-emerald-600">
                            {formatCurrency(c.agent_id === agentId ? c.agent_commission : c.co_agent_commission)}
                          </TableCell>
                          <TableCell className="text-right text-sm font-mono text-blue-600">{formatCurrency(c.brokerage_commission)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insurance">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Insurance Commissions — {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {insurance_commissions.length === 0 ? (
                <p className="text-center py-8 text-slate-400">
                  {insurance_agent ? `No insurance commissions for ${selectedYear}` : 'No insurance license linked to this agent'}
                </p>
              ) : (
                <div className="overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Carrier</TableHead>
                        <TableHead>Insured</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Premium</TableHead>
                        <TableHead className="text-right">Amount Due</TableHead>
                        <TableHead className="text-right">Agent Comm.</TableHead>
                        <TableHead className="text-right">Brokerage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {insurance_commissions.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">{c.transaction_date || c.paid_to_agent_date}</TableCell>
                          <TableCell className="text-sm font-medium">{c.carrier}</TableCell>
                          <TableCell className="text-sm max-w-[150px] truncate">{c.insured_name}</TableCell>
                          <TableCell className="text-sm">{c.category}</TableCell>
                          <TableCell className="text-sm">{c.transaction_type}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{formatCurrency(c.basis_amount)}</TableCell>
                          <TableCell className="text-right text-sm font-mono">{formatCurrency(c.amount_due)}</TableCell>
                          <TableCell className="text-right text-sm font-mono text-emerald-600">{formatCurrency(c.agent_comm)}</TableCell>
                          <TableCell className="text-right text-sm font-mono text-blue-600">{formatCurrency(c.net_comm_to_brokerage)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
